import React, { useCallback, useEffect, useImperativeHandle, useRef } from "react";

// ThatOpen / IFC.js
import * as FRAGS from "@thatopen/fragments";
import * as THREE from "three";

import { Line, MeasureData } from "@treegram/viewer-core";
import { DrawPolygonTool } from "@treegram/tools-draw-polygon";
import { SaveTool } from "@treegram/tools-save";
import { DrawLineTool } from "@treegram/tools-draw-line";
import { HighlighterTool } from "@treegram/tools-highlighter";

import useModelingToolsContext from "@/services/context/viewer/modeling-tools/use-modeling-tools-context.ts";
import useStoreyMeasuresContext from "../context/measures/use-storey-measures-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useViewerItemsVisibilityContext from "@/services/context/viewer/items-visibility/use-viewer-items-visibility-context.ts";
import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";

import { ModelingType } from "@/services/context/viewer/modeling-tools/modeling-tools-context-provider.tsx";

import useStoreyMeasurementContext
    from "@/pages/takeoff/layouts/measures/layouts/storey-measures/context/measurement/use-storey-measurement-context.ts";
import ViewerCanvas from "@/services/context/viewer/instance/viewer-canvas.tsx";

export type Measure3dViewerRef = {

    save: () => Promise<FRAGS.FragmentsModel>;
    removeMeasure: (measureId: string) => void;

    highlightGeometries: (geometries: string[], ignoreSelection: boolean) => void;

    recalculateDataInformations: (measureTypeId: string, measureDatas: MeasureData[]) => void;

    setSnappingEnabled: (isSnappingEnabled: boolean) => void;
    startMeasurement: () => Promise<void>;
    stopMeasurement: () => Promise<void>;
    clearMeasuredLines: () => void;
};

const Measure3dViewerContainer = React.forwardRef<Measure3dViewerRef>((_, ref) => {
    const { getMeasureType, getDrawingTypeFromMeasureTypeId } = useProjectMeasureContext();
    const { measures, isSaving, selectedMeasures, removeMeasure, handleGeometriesSelected } = useStoreyMeasuresContext();
    const {
        initialiseViewer,
        isInDrawingMode,
        isInEditingMode,
        drawingType,
    } = useModelingToolsContext();
    const { subscribeVisibilityChanged } = useViewerItemsVisibilityContext();
    const { addMeasuredLine } = useStoreyMeasurementContext();

    const { viewer, isLoading, getTool, getToolByKeyName } = useViewerContext();

    const tempIds = useRef<Map<string, string[]>>(new Map<string, string[]>());
    const alreadyLoaded = useRef<boolean>(false);

    const isSnappingEnabled = useRef<boolean>(true);

    const viewerGeometries = useRef<string[]>(measures.flatMap(m => m.geometryRepresentations.map(g => g.geometryId)));

    const ignoreSelectionEventsUntil = useRef(0);

    useEffect(() => {
        viewerGeometries.current = measures.flatMap(m => m.geometryRepresentations.map(g => g.geometryId));
    }, [measures]);

    useEffect(() => {
        if (viewer && !isLoading) {
            initialiseViewer(viewer);
            alreadyLoaded.current = true;
        }
    }, [viewer, isLoading, initialiseViewer]);

    const deleteGeometries = useCallback((type: ModelingType, geometryIds: string[]) => {
        switch (type) {
            case ModelingType.Point: {
                break;
            }
            case ModelingType.Line: {
                getTool(DrawLineTool)?.deleteMeasures(geometryIds);
                viewer?.prepareItemsToDelete(geometryIds);
                break;
            }
            case ModelingType.Polyline: {
                break;
            }
            case ModelingType.Surface: {
                getTool(DrawPolygonTool)?.deleteMeasures(geometryIds);
                viewer?.prepareItemsToDelete(geometryIds);
                break;
            }

            default: break;
        }
    }, [getTool, viewer]);

    const handleMeasureLineEnded = useCallback(async (e: Line) => {
        if (e == null) return;
        const val = e.value;
        addMeasuredLine(val);
    }, [addMeasuredLine]);

    const handleRemoveDataFromViewer = useCallback((measureId: string) => {
        if (viewer == null) return;

        if (isInDrawingMode) {
            switch (drawingType) {
                case ModelingType.Line: {
                    getTool(DrawLineTool)?.clear();
                    break;
                }
                case ModelingType.Surface:
                    break;
                case ModelingType.Polyline: {
                    getTool(DrawPolygonTool)?.clear();
                    break;
                }
                case ModelingType.Point:
                    break;
                default:
                    break;
            }
        } else {
            const measure = measures.find(m => m.measureId === measureId);
            if (measure == null) return;
            const type = getDrawingTypeFromMeasureTypeId(measure.measureTypeId);
            deleteGeometries(type, measure.geometryRepresentations.map(gr => gr.geometryId));
        }
    }, [viewer, isInDrawingMode, drawingType, getTool, measures, getDrawingTypeFromMeasureTypeId, deleteGeometries]);

    /* --------------- MISE EN EVIDENCE --------------- */

    useEffect(() => {
        if (isLoading) return undefined;
        const highlighter = getTool(HighlighterTool);
        if (highlighter == null) return undefined;
        highlighter.onItemsSelected.add(handleGeometriesSelected);

        return () => {
            highlighter.onItemsSelected.remove(handleGeometriesSelected);
        };
    }, [getTool, handleGeometriesSelected, isLoading]);

    useEffect(() => {
        if (isLoading) return;
        const highlighter = getTool(HighlighterTool);
        if (highlighter == null) return;

        if (selectedMeasures != null && selectedMeasures.measures.length > 0) {
            const geometries = selectedMeasures.measures.flatMap(measure =>
                measure.geometryRepresentations.map(geometry => geometry.geometryId)
            );

            if (geometries.length > 0) {
                ignoreSelectionEventsUntil.current = Date.now() + 150;
                highlighter.highlightItems(geometries);
            }
        }
    }, [getTool, isLoading, selectedMeasures]);

    /* GESTION SUPPRESSION DEPUIS SELECTION */

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const selectedMeasure = selectedMeasures?.measures.length === 1
                ? selectedMeasures.measures[0]
                : undefined;

            if (e.key !== "Delete" || selectedMeasure == null || isSaving || isInDrawingMode || isInEditingMode) return;

            removeMeasure(selectedMeasure.measureId);
            handleRemoveDataFromViewer(selectedMeasure.measureId);
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isSaving, isInDrawingMode, isInEditingMode, removeMeasure, handleRemoveDataFromViewer, selectedMeasures?.measures]);

    useEffect(() => subscribeVisibilityChanged(async ({ action, items }) => {
        if (viewer == null || isLoading) return;

        if (action === "add") {
            await viewer.showItems(items);
        }

        if (action === "remove") {
            await viewer.hideItems(items);
        }
    }), [subscribeVisibilityChanged, viewer, isLoading]);

    useImperativeHandle(ref, () => ({
        save: async () => {
            const saveTool = getTool(SaveTool);
            if (!saveTool || !viewer) {
                throw new Error("Le viewer n'est pas prêt pour la sauvegarde");
            }
            tempIds.current = new Map<string, string[]>();
            return saveTool.save();
        },
        highlightGeometries: (geometries: string[], ignoreSelection) => {
            if (ignoreSelection) {
                ignoreSelectionEventsUntil.current = Date.now() + 150;
            }

            getTool(HighlighterTool)?.highlightItems(geometries);
        },
        recalculateDataInformations: (measureTypeId: string, measureDatas: MeasureData[]) => {
            if (!viewer) {
                throw new Error("Le viewer n'est pas encore prêt");
            }

            const measureType = getMeasureType(measureTypeId);
            if (!measureType || !measureType.dataInformations) {
                throw new Error("Le type de métré n'existe pas");
            }

            viewer.recalculateDataInformations(measureDatas, measureType.dataInformations);
            measureDatas.forEach(md => {
                viewer.prepareItemToUpdate(md);
            });
        },
        removeMeasure: async (measureId: string) => {
            handleRemoveDataFromViewer(measureId);
        },
        startMeasurement: async () => {
            const measureTool = getToolByKeyName<DrawLineTool>("measureTool");
            const highlighter = getTool(HighlighterTool);
            if (measureTool == null) return;

            await highlighter?.deactivate();

            await measureTool.activate();
            measureTool.setColor(new THREE.Color("#286FB7"));
            measureTool.setAlpha(1);
            measureTool.setMeasureRepresentation("LINE_IMPRINT");
            measureTool.setSnappings(isSnappingEnabled.current ? [FRAGS.SnappingClass.LINE, FRAGS.SnappingClass.POINT, FRAGS.SnappingClass.FACE] : []);

            measureTool.onEndedCreation.add(handleMeasureLineEnded);
            measureTool.draw();
        },
        stopMeasurement: async () => {
            const measureTool = getToolByKeyName<DrawLineTool>("measureTool");
            const highlighter = getTool(HighlighterTool);
            if (measureTool == null) return;

            measureTool.onEndedCreation.remove(handleMeasureLineEnded);
            await measureTool.deactivate();
            measureTool.clear();

            if (highlighter != null) {
                await highlighter.activate();
                highlighter.onItemsSelected.add(handleGeometriesSelected);
            }
        },
        clearMeasuredLines: () => {
            const measureTool = getToolByKeyName<DrawLineTool>("measureTool");
            if (measureTool == null) return;

            measureTool.clear();
        },
        setSnappingEnabled: (value: boolean) => {
            isSnappingEnabled.current = value;
            const snappings = isSnappingEnabled.current ? [FRAGS.SnappingClass.LINE, FRAGS.SnappingClass.POINT, FRAGS.SnappingClass.FACE] : [];
            if (isInDrawingMode) {
                const lineTool = getTool(DrawLineTool);
                const polygonTool = getTool(DrawPolygonTool);

                lineTool?.setSnappings(snappings);
                polygonTool?.setSnappings(snappings);
            }
            if (isInEditingMode) {
                const lineToolEditing = getToolByKeyName<DrawLineTool>("drawLineToolEditing");
                const polygonToolEditing = getToolByKeyName<DrawPolygonTool>("drawPolygonToolEditing");

                lineToolEditing?.setSnappings(snappings);
                polygonToolEditing?.setSnappings(snappings);
            }
            const measureTool = getToolByKeyName<DrawLineTool>("measureTool");
            measureTool?.setSnappings(snappings);
        }
    }), [getTool, viewer, getMeasureType, handleRemoveDataFromViewer, getToolByKeyName, handleMeasureLineEnded, handleGeometriesSelected, isInDrawingMode, isInEditingMode]);

    return (
        <ViewerCanvas showLoading={!alreadyLoaded.current} />
    );
});

export default Measure3dViewerContainer;
